import { useEffect } from 'react';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { getMyBookings } from '@hqo/service-booking-client';
import { BookingRO, EventBookingStatus } from '@hqo/service-booking-types';

import { ERROR_DEFAULT } from 'app/shared/consts/error-codes';
import { useApi } from 'app/shared/contexts/api';
import { useErrors } from 'app/shared/contexts/errors';
import { useServiceBookingAuth } from 'app/shared/hooks/use-service-booking-auth';
import { useUser } from 'app/shared/contexts/user';
import { retryOnAuthError } from 'app/shared/auth';
import { isPaymentSettling } from 'app/shared/utils/settling-payment';

export const BOOKINGS_QUERY_KEY = 'bookings';

/** Refetch cadence while a booking this tab paid for is still PENDING_PAYMENT. */
export const SETTLING_REFETCH_MS = 3_000;

export interface UseBookingsQueryParams {
  /** Filter to this event uuid. The endpoint scopes to the caller by token. */
  eventUuid?: string;
}

/**
 * Cache key includes the user uuid so a host-driven auth swap (session
 * refresh, kiosk handover) doesn't serve the previous user's bookings
 * from the React Query cache.
 */
export const getUseBookingsQueryKey = (
  { eventUuid }: UseBookingsQueryParams,
  userUuid: string | undefined,
): QueryKey => [BOOKINGS_QUERY_KEY, userUuid ?? '', eventUuid ?? ''];

/**
 * Statuses the EventDetail page wants to know about. The active set drives
 * the CTA logic; `CANCELLED` is included so the calendar block can show a
 * "Cancelled" badge for a user's previously-cancelled booking on a fresh
 * page load. `CANCELLED` also carries the "missed your spot" case
 * (cancellationReason WAITLIST_NOT_CONFIRMED), which the page detects on load.
 * Page-level helpers split active vs all when deriving the primary booking.
 */
const VISIBLE_BOOKING_STATUSES: EventBookingStatus[] = [
  EventBookingStatus.CONFIRMED,
  EventBookingStatus.WAITLIST,
  EventBookingStatus.CHECKED_IN,
  EventBookingStatus.PENDING,
  EventBookingStatus.PENDING_PAYMENT,
  EventBookingStatus.WAITING_FOR_CONFIRMATION,
  EventBookingStatus.CANCELLED,
];

export interface UseBookingsQueryResponse {
  bookings: BookingRO[];
  error: Error | null;
  isLoading: boolean;
  isSuccess: boolean;
}

/**
 * Lists the caller's bookings, optionally narrowed to a single event. The
 * SBS endpoint scopes by token (no need to pass a userUuid); the FE filter
 * here further narrows to active statuses so the page's CTA derivation
 * doesn't have to special-case `CANCELLED` rows.
 *
 * Failures surface as a recoverable error (modal channel) — the page can
 * still render the event details without booking context.
 */
export const useBookingsQuery = (params: UseBookingsQueryParams = {}): UseBookingsQueryResponse => {
  const { apiUrl } = useApi();
  const getAuthOptions = useServiceBookingAuth();
  const { addError, hideErrors } = useErrors();
  const { user } = useUser();

  const { data, error, isLoading, isSuccess } = useQuery<BookingRO[], Error>({
    queryKey: getUseBookingsQueryKey(params, user?.uuid),
    queryFn: async () => {
      const auth = await getAuthOptions();
      const result = await getMyBookings(
        {
          baseUrl: apiUrl,
          params: {
            ...(params.eventUuid && { eventUuid: [params.eventUuid] }),
            status: VISIBLE_BOOKING_STATUSES,
          },
        },
        auth,
      );
      if (!result.isSuccess()) {
        throw result.errorData;
      }
      return result.value.data;
    },
    // Gate on `eventUuid` so the page's loading-spinner phase (where
    // `serviceUuid` is still resolving via openParams) doesn't fire an
    // unfiltered request that returns ALL of the user's bookings —
    // `findActiveBooking` would then race against an unrelated event's
    // row and surface the wrong state briefly.
    enabled: !!apiUrl && !!user?.uuid && !!params.eventUuid,
    retry: retryOnAuthError,
    // A paid booking stays PENDING_PAYMENT until SBS hears about the payment
    // (SMB-1785). Poll until it settles so the page flips to Registered on its
    // own; the settling flag expires, so this stops even if it never does.
    refetchInterval: ({ state }) => (
      state.data?.some((b) => b.status === EventBookingStatus.PENDING_PAYMENT && isPaymentSettling(b.uuid))
        ? SETTLING_REFETCH_MS
        : false
    ),
  });

  useEffect(() => {
    if (error) {
      addError(BOOKINGS_QUERY_KEY, { error, errorCode: ERROR_DEFAULT });
    } else {
      hideErrors(BOOKINGS_QUERY_KEY);
    }
  }, [error, addError, hideErrors]);

  return {
    bookings: data ?? [],
    error: error ?? null,
    isLoading,
    isSuccess,
  };
};
